import { fetchWithTokenAndRegion } from '@adsk/acs-sc-web-platform-api';
import { getOrCreateGlobalStore } from '@adsk/alloy-react-context';

import {
  readCachedEntities,
  readCachedData,
  readCachedDataByUrl,
  writeCachedEntities,
  writeCachedData,
  writeCachedDataByUrl,
} from './cacheHelpers';
import { removeOldDuplicatesBy } from './removeOldDuplicatesBy';

type BaseEntity = {
  id: string;
};

type BaseEntityWithCustomId<K extends string = 'id'> = {
  [P in K]: string;
};

export interface BaseDataStoreError extends Error {
  status?: number;
  redirectUrl?: string;
}

export type DataModifications<T extends BaseEntity> = {
  addedEntities: T[];
  updatedEntities: Map<string, T>;
  removedEntityIds: Set<string>;
};

type BaseDataStore<
  T extends BaseEntityWithCustomId<K>,
  K extends string = 'id',
> = {
  // Core data state
  entities: T[];
  error: BaseDataStoreError | null;
  loading: boolean;
  _started: boolean;
  url: string | null;
  initialUrl: string | null;
  // Core data actions
  addEntity: (entity: T) => void;
  addEntities: (entities: T[]) => void;
  updateEntity: (entity: T) => void;
  removeEntityById: (id: string) => void;
};

type BaseControlStore = {
  // Control actions
  fetchPage: (url: string | null, eager?: boolean) => void;
  retry: () => void;
};

export type BaseCombinedStore<
  T extends BaseEntityWithCustomId<K>,
  K extends string = 'id',
> = BaseDataStore<T, K> & BaseControlStore;

/**
 * Creates a reusable combined store with data management and pagination control.
 * This is the main factory function for creating entity stores with persistence.
 *
 * ## Caching Strategy
 *
 * The store uses sessionStorage for caching with the following behavior:
 * - **Cache Key Format**: `${contextName}_${url}` for each page URL
 * - **Cache Lifecycle**: Cache persists for the browser session
 * - **Cache Invalidation**: Cache is automatically overwritten on successful fetch
 * - **Cache Loading**: Cached data is loaded immediately before fetch to provide instant UI feedback
 * - **Resilience**: Uses safeSessionStorage wrapper that gracefully handles SSR, quota exceeded, and provides in-memory fallback
 *
 * ## Fetch Behavior
 *
 * - The store manages an internal `_started` flag to prevent duplicate fetches
 * - `fetchPage(url, eager)` respects `_started`; when `eager` is true it bypasses the guard to continue pagination chains
 * - First page (non-eager) replaces entities, subsequent pages (eager) append and deduplicate
 * - Cache is written after each successful page fetch
 * - Accumulated cache is maintained at the `initialUrl` key for the full paginated dataset
 *
 * ## Error Handling
 *
 * - Errors are properly typed as BaseDataStoreError
 * - Failed fetches preserve existing entities
 * - Cache failures are logged in development but don't throw
 *
 * @template T - The entity type that extends BaseEntityWithCustomId
 * @template K - The key name for the ID field (defaults to 'id')
 * @param config - Configuration object
 * @param config.contextName - Unique identifier for the store context, used for cache keys
 * @param config.idField - The field name to use as the entity ID (defaults to 'id')
 * @param config.fetchFunction - Optional custom fetch function (defaults to fetchWithTokenAndRegion)
 * @returns A Zustand store with persistence, data management, and pagination control
 */
export type PaginatedResponse<T> = {
  results: T[];
  pagination: { nextUrl?: string | null };
};

export const createPaginatedStore = <
  T extends BaseEntityWithCustomId<K>,
  K extends string = 'id',
>({
  contextName,
  idField = 'id' as K,
  fetchFunction = (url: string) =>
    fetchWithTokenAndRegion<PaginatedResponse<T>>(url),
}: {
  contextName: string;
  idField?: K;
  fetchFunction?: (url: string) => Promise<PaginatedResponse<T>>;
}) =>
  getOrCreateGlobalStore<BaseCombinedStore<T, K>>(
    (set, get) => ({
      // Data state (from BaseDataStore)
      entities: [],
      error: null,
      loading: true,
      _started: false,
      url: null,
      initialUrl: null,

      // Data actions
      addEntity: (entity) => {
        const { entities, initialUrl } = get();
        const existingIndex = entities.findIndex(
          (e) => e[idField] === entity[idField],
        );

        if (existingIndex >= 0) {
          // Replace existing entity
          const newEntities = [...entities];
          newEntities[existingIndex] = entity;
          set({ entities: newEntities });
        } else {
          // Add new entity
          set({ entities: [...entities, entity] });
        }

        // Update cache with new entity
        if (initialUrl) {
          writeCachedEntities(contextName, initialUrl, get().entities);
        }
      },

      addEntities: (newEntities) => {
        const { entities, initialUrl } = get();
        const combinedEntities = [...entities, ...newEntities];
        const deduplicatedEntities = removeOldDuplicatesBy(
          combinedEntities,
          (entity) => entity[idField],
        );
        set({ entities: deduplicatedEntities });

        // Update cache with new entity
        if (initialUrl) {
          writeCachedEntities(contextName, initialUrl, get().entities);
        }
      },

      updateEntity: (entity) => {
        const { entities, initialUrl } = get();
        const updatedEntities = entities.map((e) =>
          e[idField] === entity[idField] ? entity : e,
        );
        set({ entities: updatedEntities });

        // Update cache with new entity
        if (initialUrl) {
          writeCachedEntities(contextName, initialUrl, get().entities);
        }
      },

      removeEntityById: (id) => {
        const { entities, initialUrl } = get();
        const filteredEntities = entities.filter((e) => e[idField] !== id);
        set({ entities: filteredEntities });

        // Update cache with new entity
        if (initialUrl) {
          writeCachedEntities(contextName, initialUrl, get().entities);
        }
      },

      fetchPage: (url, eager = false) => {
        if (!url) {
          set({ loading: false });
          return;
        }

        // Set _started immediately to prevent race conditions
        // Check must happen before async operation begins
        const currentState = get();
        if (currentState._started && !eager) {
          return;
        }

        // Set loading state and guard flag atomically
        set({ loading: true, error: null, _started: true });

        if (!eager) {
          set({ url, initialUrl: url });

          // Load cached data immediately before fetching (only if not already loading)
          // This provides instant UI feedback while fresh data loads
          const cachedEntities = readCachedEntities<T>(contextName, url);
          if (cachedEntities && cachedEntities.length > 0) {
            set({ entities: cachedEntities });
          }
        }

        const performFetch = async () => {
          try {
            const response = await fetchFunction(url);
            const { results, pagination } = response;

            const { entities: currentEntities, initialUrl } = get();

            // Replace entities for first page (non-eager), append for subsequent pages (eager)
            const deduplicatedEntities = eager
              ? removeOldDuplicatesBy(
                  [...currentEntities, ...results],
                  (entity) => entity[idField],
                )
              : results;

            set({
              entities: deduplicatedEntities,
              loading: Boolean(pagination.nextUrl), // keep loading state until all pages are fetched (to avoid flickering)
            });

            // Cache this page response immediately
            writeCachedEntities(contextName, url, deduplicatedEntities);

            // Update accumulated cache after each page
            if (initialUrl) {
              writeCachedEntities(
                contextName,
                initialUrl,
                deduplicatedEntities,
              );
            }

            // Continue fetching if there's a next page
            if (pagination.nextUrl) {
              await get().fetchPage(pagination.nextUrl, true);
            }
          } catch (error) {
            set({
              error: error as Error,
              loading: false,
              entities: get().entities || [],
            });
          }
        };

        performFetch().catch((unhandledError) => {
          console.error(
            `[${contextName}] Unhandled error in fetchPage:`,
            unhandledError,
          );
          set({ error: unhandledError as Error, loading: false });
        });
      },

      retry: () => {
        const { fetchPage, url } = get();
        set({ _started: false });
        fetchPage(url, false);
      },
    }),
    contextName,
  );

/**
 * Unified data store interface for managing state and loading states.
 * This interface provides a consistent API for all data providers in the application.
 *
 * ## Caching Strategy
 *
 * The store uses sessionStorage for caching with the following behavior:
 * - **Cache Key Format**: `${contextName}` when no url is provided, or `${contextName}_${url}` when a url is used
 * - **Cache Lifecycle**: Cache persists for the browser session
 * - **Cache Invalidation**: Cache is automatically overwritten on successful fetch.
 *   When a url is provided, each url gets its own cache entry, so changing the url
 *   ensures stale data from a previous url is never served.
 * - **Cache Loading**: Cached data is loaded immediately before fetch to provide instant UI feedback
 * - **Resilience**: Uses safeSessionStorage wrapper that gracefully handles SSR, quota exceeded, and provides in-memory fallback
 *
 * ## Fetch Behavior
 *
 * - The store manages an internal `_started` flag to prevent duplicate fetches
 * - `fetchData(url?)` respects `_started` and will skip if already fetching with the same url
 * - When called with a new url, `_started` is reset, data is cleared, and a fresh fetch begins
 * - Cache is written after each successful fetch
 * - Failed fetches preserve existing data state
 *
 * ## Error Handling
 *
 * - Errors are properly typed as BaseDataStoreError
 * - Failed fetches preserve existing data
 * - Cache failures are logged in development but don't throw
 *
 * @template T - The type of data being stored
 *
 * @property {T | null} data - The current data state. Null when no data is loaded or error occurred
 * @property {BaseDataStoreError | null} error - Current error state. Null when no error has occurred
 * @property {boolean} loading - Whether a fetch operation is currently in progress
 * @property {boolean} _started - Whether the store has initiated a fetch in this session (internal flag)
 * @property {string | null} _url - The current cache key used for url-based invalidation (internal)
 * @property {(data: T | null) => void} setData - Function to manually update the data state
 * @property {(url?: string) => void} fetchData - Function to fetch data. Pass an optional url
 *   to enable cache-breaking when the url changes.
 * @property {() => void} retry - Function to retry failed requests (resets `_started` internally)
 */
export type DataStore<T> = {
  // Core data state
  data: T | null;
  error: BaseDataStoreError | null;
  loading: boolean;
  _started: boolean;
  _url: string | null;
  setData: (data: T | null) => void;

  // Fetch methods
  fetchData: (url?: string) => void;
  retry: () => void;
};

/**
 * Creates a unified data store for managing single-entity state with async operations.
 *
 * This factory function creates a Zustand store that provides a consistent interface
 * for data fetching, error handling, and loading states. The store includes session
 * storage persistence and guards duplicate calls using the `_started` flag.
 *
 * ## Caching Strategy
 *
 * See {@link DataStore} interface documentation for detailed caching behavior.
 *
 * ## Key-Based Cache Invalidation
 *
 * Pass a `url` to `fetchData(url)` to scope the cache and duplicate-fetch guard to
 * that url.
 *
 * @template T - The type of data entity being managed by the store
 * @param config - Configuration object for the data store
 * @param config.contextName - Unique identifier for the store context, used for cache keys
 * @param config.fetchFunction - Async function that fetches data (no arguments)
 *
 * @returns A Zustand store factory function that creates DataStore instances
 *
 * @example
 * ```typescript
 * // Without url
 * const createUserStore = createDataStore<User>({
 *   contextName: 'UserContext',
 *   fetchFunction: async () => fetchUser(),
 * });
 * const store = createUserStore();
 * const { fetchData } = store();
 * fetchData();
 *
 * // With url key-based invalidation
 * const createAccountUserStore = createDataStore<AccountUser>({
 *   contextName: 'AccountUserContext',
 *   fetchFunction: async (url) => fetchAccountUser(url),
 * });
 * const store = createAccountUserStore();
 * const { fetchData } = store();
 * fetchData(url); // re-fetches when url changes
 * ```
 */
export const createDataStore = <T>({
  contextName,
  fetchFunction,
}: {
  contextName: string;
  fetchFunction: (url?: string) => Promise<T>;
}) =>
  getOrCreateGlobalStore<DataStore<T>>(
    (set, get) => ({
      // Core data state
      data: null,
      error: null,
      loading: true,
      _started: false,
      _url: null,
      setData: (data: T | null) => {
        set({ data });

        const { _url } = get();
        if (_url) {
          writeCachedDataByUrl(contextName, _url, data);
        } else {
          writeCachedData(contextName, data);
        }
      },

      fetchData: (url?: string) => {
        const currentState = get();
        const normalizedUrl = url ?? null;
        const urlChanged = normalizedUrl !== currentState._url;

        // Set _started immediately to prevent race conditions
        // Check must happen before async operation begins
        if (currentState._started && !urlChanged) {
          return;
        }

        // Set loading state and guard flag atomically
        // Clear data if url changed
        set({
          _started: true,
          loading: true,
          error: null,
          ...(urlChanged && { _url: normalizedUrl, data: null }),
        });

        // Load cached data immediately before fetching (only if not already loading)
        // This provides instant UI feedback while fresh data loads
        const cachedData =
          normalizedUrl !== null
            ? readCachedDataByUrl<T>(contextName, normalizedUrl)
            : readCachedData<T>(contextName);
        if (cachedData !== null) {
          set({ data: cachedData });
        }

        const performFetch = async () => {
          try {
            const response = await fetchFunction(url);

            // Write to cache after successful fetch
            if (normalizedUrl !== null) {
              writeCachedDataByUrl(contextName, normalizedUrl, response);
            } else {
              writeCachedData(contextName, response);
            }

            if (get()._url !== normalizedUrl) {
              // If url changed during fetch, we should not overwrite the data with stale response
              return;
            }

            set({
              data: response,
              error: null,
              loading: false,
            });
          } catch (error) {
            set({
              error: error as Error,
              loading: false,
            });
          }
        };

        performFetch().catch((unhandledError) => {
          console.error(
            `[${contextName}] Unhandled error in fetchData:`,
            unhandledError,
          );
          set({ error: unhandledError as Error, loading: false });
        });
      },

      retry: () => {
        const { fetchData, _url } = get();
        set({ _started: false });
        fetchData(_url ?? undefined);
      },
    }),
    contextName,
  );

export const fetchWithUrlValidation = <T>(
  contextName: string,
): ((url?: string) => Promise<T>) => {
  return (url?: string) => {
    if (!url) {
      throw new Error(`[${contextName}] No url provided in fetch`);
    }
    return fetchWithTokenAndRegion<T>(url);
  };
};
